import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import TagInput from './TagInput';

interface StakeProject {
  _id: string;
  title: string;
  description: string;
  imageUrl?: string;
  network: string;
  status: string;
  isPublished: boolean;
  featured?: boolean;
  slug: string;
  commission: string;
  apy: string;
  minimumStake: string;
  activationPeriod: string;
  undelegatePeriod: string;
  // NEW FIELDS
  stakerAddress?: string;
  stakingLink?: string;
  ticker?: string;
  // Price API fields
  enablePriceAPI?: boolean;
  coingeckoId?: string;
  currentPrice?: number;
  lastPriceUpdate?: string;
  // Additional fields from Services
  projectUrl?: string;
  links?: {
    github?: string;
    twitter?: string;
    discord?: string;
    website?: string;
  };
  tags?: string[];
  // Service blocks configuration
  serviceBlocks?: {
    explorer?: {
      enabled?: boolean;
      title?: string;
      description?: string;
      link?: string;
    };
    monitoring?: {
      enabled?: boolean;
      title?: string;
      description?: string;
      link?: string;
    };
    validator?: {
      enabled?: boolean;
      title?: string;
      description?: string;
      link?: string;
    };
    analytics?: {
      enabled?: boolean;
      title?: string;
      description?: string;
      link?: string;
    };
  };
}

interface StakeFormData {
  title: string;
  description: string;
  imageUrl: string;
  network: string;
  isPublished: boolean;
  featured: boolean;
  commission: string;
  apy: string;
  minimumStake: string;
  activationPeriod: string;
  undelegatePeriod: string;
  // NEW FIELDS
  stakerAddress: string;
  stakingLink: string;
  ticker: string;
  // Price API fields
  enablePriceAPI: boolean;
  coingeckoId: string;
  // Additional fields from Services
  projectUrl: string;
  githubUrl: string;
  twitterUrl: string;
  discordUrl: string;
  websiteUrl: string;
  tags: string[];
  // Service blocks configuration
  explorerEnabled: boolean;
  explorerTitle: string;
  explorerDescription: string;
  explorerLink: string;
  monitoringEnabled: boolean;
  monitoringTitle: string;
  monitoringDescription: string;
  monitoringLink: string;
  validatorEnabled: boolean;
  validatorTitle: string;
  validatorDescription: string;
  validatorLink: string;
  analyticsEnabled: boolean;
  analyticsTitle: string;
  analyticsDescription: string;
  analyticsLink: string;
}

const StakeSection: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState<StakeProject | null>(null);
  const [showLogoSelector, setShowLogoSelector] = useState(false);
  
  // Стейк проекти з бази даних
  const [stakeProjects, setStakeProjects] = useState<StakeProject[]>([]);
  
  // Стан для існуючих проектів (для пошуку логотипів)
  const [existingProjects, setExistingProjects] = useState<any[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  const [formData, setFormData] = useState<StakeFormData>({
    title: '',
    description: '',
    imageUrl: '',
    network: 'mainnet',
    isPublished: true,
    featured: false,
    commission: '',
    apy: '',
    minimumStake: '',
    activationPeriod: '',
    undelegatePeriod: '',
    stakerAddress: '',
    stakingLink: '',
    ticker: '',
    enablePriceAPI: false,
    coingeckoId: '',
    projectUrl: '',
    githubUrl: '',
    twitterUrl: '',
    discordUrl: '',
    websiteUrl: '',
    tags: [],
    // Service blocks configuration
    explorerEnabled: true,
    explorerTitle: 'DUSKPROOF Blockchain Explorer',
    explorerDescription: 'Explore with Precision. A next-gen explorer built for seamless navigation across networks — track blocks, validators, and delegations with clarity and speed.',
    explorerLink: '',
    monitoringEnabled: true,
    monitoringTitle: 'DUSKPROOF Alert System',
    monitoringDescription: 'Stay Informed, Instantly. AI-enhanced Telegram bot that monitors your validator, alerts you about missed blocks, version updates, and governance proposals — fully customizable for your setup.',
    monitoringLink: '',
    validatorEnabled: true,
    validatorTitle: 'DUSKPROOF Blockchain Services',
    validatorDescription: 'We maintain a full suite of infrastructure for mainnets and testnets — including RPC, API, gRPC, snapshots, and state sync. Secure, stable, and constantly updated.',
    validatorLink: '',
    analyticsEnabled: true,
    analyticsTitle: 'DUSKPROOF Analytics Dashboard',
    analyticsDescription: 'See Beyond the Metrics. An advanced analytics suite delivering insights into validator performance, uptime, governance, and on-chain data availability — all in one unified view.',
    analyticsLink: '',
  });

  // Функція для завантаження стейк проектів з бази даних (використовуємо admin endpoint щоб бачити ВСІ проекти)
  const fetchStakeProjects = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      
      // Використовуємо admin endpoint, щоб бачити і непубліковані проекти
      // Це критично - публічний endpoint фільтрує isPublished=true,
      // тому непубліковані проекти "зникають" з адмінки, але залишаються в БД
      const response = await axios.get(`${apiUrl}/api/projects/admin?category=Stake&limit=50`, {
        withCredentials: true
      });
      
      if (response.data.success && response.data.data) {
        // Фільтруємо проекти з валідними полями
        const validProjects = response.data.data.projects.filter((project: any) => 
          project && project.title && project.description
        );
        setStakeProjects(validProjects);
        console.log('Loaded stake projects from admin endpoint:', validProjects.length);
      }
    } catch (error) {
      console.error('Error fetching stake projects from admin endpoint, falling back to public:', error);
      // Fallback до публічного endpoint
      try {
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        const response = await axios.get(`${apiUrl}/api/projects?category=Stake`);
        if (response.data.success && response.data.data) {
          const validProjects = response.data.data.projects.filter((project: any) => 
            project && project.title && project.description
          );
          setStakeProjects(validProjects);
          console.log('Loaded stake projects from public endpoint:', validProjects.length);
        }
      } catch (fallbackError) {
        console.error('Error fetching stake projects:', fallbackError);
      }
    }
  };

  // Завантажуємо стейк проекти з бази даних
  useEffect(() => {
    fetchStakeProjects();
  }, []);

  // Завантажуємо існуючі проекти для пошуку логотипів
  useEffect(() => {
    const fetchExistingProjects = async () => {
      setProjectsLoading(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        
        // Завжди спробуємо admin endpoint спочатку
        try {
          console.log('🔑 Trying admin endpoint for all projects (including Admin Only)');
          
          // 🔐 SECURITY: Use httpOnly cookies instead of localStorage tokens
          const response = await axios.get(`${apiUrl}/api/projects/admin`, {
            withCredentials: true
          });
          console.log('📡 Admin endpoint response:', response.data);
          if (response.data.success && response.data.data) {
            // Використовуємо всі проекти (включно з Admin Only) для пошуку логотипів
            setExistingProjects(response.data.data.projects);
            console.log('✅ Loaded projects for logo search (with Admin Only):', response.data.data.projects.length);
            console.log('📋 Project titles and statuses:', response.data.data.projects.map((p: any) => `"${p.title}" (${p.status})`));
            return;
          }
        } catch (adminError) {
          console.error('❌ Admin endpoint failed:', (adminError as any)?.response?.status, (adminError as any)?.response?.data);
          console.warn('⚠️ Falling back to public endpoint (no Admin Only projects)');
        }
        
        // Якщо admin endpoint не працює, завантажуємо ВСІ проекти через публічний endpoint
        console.log('🔍 Trying to get ALL projects via public endpoint...');
        
        // Спочатку отримуємо опубліковані проекти
        const publishedResponse = await axios.get(`${apiUrl}/api/projects?status=active&isPublished=true`);
        
        // Потім отримуємо неопубліковані (Admin Only) проекти
        const adminOnlyResponse = await axios.get(`${apiUrl}/api/projects?status=active&isPublished=false`);
        
        const allProjects = [];
        
        if (publishedResponse.data.success && publishedResponse.data.data) {
          allProjects.push(...publishedResponse.data.data.projects);
          console.log('✅ Published projects loaded:', publishedResponse.data.data.projects.length);
        }
        
        if (adminOnlyResponse.data.success && adminOnlyResponse.data.data) {
          allProjects.push(...adminOnlyResponse.data.data.projects);
          console.log('✅ Admin Only projects loaded:', adminOnlyResponse.data.data.projects.length);
        }
        
        if (allProjects.length > 0) {
          setExistingProjects(allProjects);
          console.log('🎉 Total projects loaded:', allProjects.length);
          console.log('📋 All projects:', allProjects.map((p: any) => `"${p.title}" (${p.isPublished ? 'published' : 'admin-only'})`));
          console.log('🔍 Non-Stake projects for auto-detection:', allProjects.filter((p: any) => p.category !== 'Stake').length);
        } else {
          console.log('❌ No projects loaded from either endpoint');
        }
      } catch (error) {
        console.error('Error fetching existing projects:', error);
        // Навіть якщо не вдалося завантажити проекти, продовжуємо роботу
        setExistingProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    };
    
    fetchExistingProjects();
  }, []);

  // Ref для відстеження попереднього стану enablePriceAPI
  const prevEnablePriceAPI = useRef(formData.enablePriceAPI);

  // Автоматичне генерування CoinGecko ID на основі назви проекту
  useEffect(() => {
    const wasJustEnabled = !prevEnablePriceAPI.current && formData.enablePriceAPI;
    prevEnablePriceAPI.current = formData.enablePriceAPI;
    
    // Генеруємо CoinGecko ID якщо:
    // 1. Це новий проект (не редагування)
    // 2. Щойно увімкнули Price API АБО є назва проекту та увімкнено API
    // 3. CoinGecko ID порожній
    const shouldGenerate = !editingProject && 
                           formData.enablePriceAPI && 
                           formData.title.trim() && 
                           !formData.coingeckoId.trim() &&
                           (wasJustEnabled || formData.title.trim());

    if (shouldGenerate) {
      const generateCoinGeckoId = (title: string) => {
        return title
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '') // Видалити спеціальні символи
          .replace(/\s+/g, '-')         // Замінити пробіли на дефіси
          .replace(/-+/g, '-')          // Замінити множинні дефіси на один
          .replace(/^-+|-+$/g, '');     // Видалити дефіси на початку та в кінці
      };

      const newCoinGeckoId = generateCoinGeckoId(formData.title);
      console.log('🔄 Auto-generating CoinGecko ID:', newCoinGeckoId, 'from title:', formData.title);
      
      setFormData(prev => ({ ...prev, coingeckoId: newCoinGeckoId }));
    }
  }, [formData.title, formData.enablePriceAPI, formData.coingeckoId, editingProject]);

  const toggleForm = () => {
    setShowForm(!showForm);
    // Якщо закриваємо форму, скидаємо редагування
    if (showForm) {
      setEditingProject(null);
      clearForm();
    }
  };

  const clearForm = () => {
    setFormData({
      title: '',
      description: '',
      imageUrl: '',
      network: 'mainnet',
      isPublished: true,
      featured: false,
      commission: '',
      apy: '',
      minimumStake: '',
      activationPeriod: '',
      undelegatePeriod: '',
      stakerAddress: '',
      stakingLink: '',
      ticker: '',
      enablePriceAPI: false,
      coingeckoId: '',
      projectUrl: '',
      githubUrl: '',
      twitterUrl: '',
      discordUrl: '',
      websiteUrl: '',
      tags: [],
      // Service blocks configuration
      explorerEnabled: true,
      explorerTitle: 'DUSKPROOF Blockchain Explorer',
      explorerDescription: 'Explore with Precision. A next-gen explorer built for seamless navigation across networks — track blocks, validators, and delegations with clarity and speed.',
      explorerLink: '',
      monitoringEnabled: true,
      monitoringTitle: 'DUSKPROOF Alert System',
      monitoringDescription: 'Stay Informed, Instantly. AI-enhanced Telegram bot that monitors your validator, alerts you about missed blocks, version updates, and governance proposals — fully customizable for your setup.',
      monitoringLink: '',
      validatorEnabled: true,
      validatorTitle: 'DUSKPROOF Blockchain Services',
      validatorDescription: 'We maintain a full suite of infrastructure for mainnets and testnets — including RPC, API, gRPC, snapshots, and state sync. Secure, stable, and constantly updated.',
      validatorLink: '',
      analyticsEnabled: true,
      analyticsTitle: 'DUSKPROOF Analytics Dashboard',
      analyticsDescription: 'See Beyond the Metrics. An advanced analytics suite delivering insights into validator performance, uptime, governance, and on-chain data availability — all in one unified view.',
      analyticsLink: '',
    });
  };

  const handleEdit = (project: StakeProject) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      imageUrl: project.imageUrl || '',
      network: project.network,
      isPublished: project.isPublished,
      featured: project.featured || false,
      commission: project.commission,
      apy: project.apy,
      minimumStake: project.minimumStake,
      activationPeriod: project.activationPeriod,
      undelegatePeriod: project.undelegatePeriod,
      stakerAddress: project.stakerAddress || '',
      stakingLink: project.stakingLink || '',
      ticker: project.ticker || '',
      enablePriceAPI: project.enablePriceAPI || false,
      coingeckoId: project.coingeckoId || '',
      projectUrl: project.projectUrl || '',
      githubUrl: project.links?.github || '',
      twitterUrl: project.links?.twitter || '',
      discordUrl: project.links?.discord || '',
      websiteUrl: project.links?.website || '',
      tags: project.tags || [],
      // Service blocks configuration
      explorerEnabled: project.serviceBlocks?.explorer?.enabled ?? true,
      explorerTitle: project.serviceBlocks?.explorer?.title || 'DUSKPROOF Blockchain Explorer',
      explorerDescription: project.serviceBlocks?.explorer?.description || 'Explore with Precision. A next-gen explorer built for seamless navigation across networks — track blocks, validators, and delegations with clarity and speed.',
      explorerLink: project.serviceBlocks?.explorer?.link || '',
      monitoringEnabled: project.serviceBlocks?.monitoring?.enabled ?? true,
      monitoringTitle: project.serviceBlocks?.monitoring?.title || 'DUSKPROOF Alert System',
      monitoringDescription: project.serviceBlocks?.monitoring?.description || 'Stay Informed, Instantly. AI-enhanced Telegram bot that monitors your validator, alerts you about missed blocks, version updates, and governance proposals — fully customizable for your setup.',
      monitoringLink: project.serviceBlocks?.monitoring?.link || '',
      validatorEnabled: project.serviceBlocks?.validator?.enabled ?? true,
      validatorTitle: project.serviceBlocks?.validator?.title || 'DUSKPROOF Blockchain Services',
      validatorDescription: project.serviceBlocks?.validator?.description || 'We maintain a full suite of infrastructure for mainnets and testnets — including RPC, API, gRPC, snapshots, and state sync. Secure, stable, and constantly updated.',
      validatorLink: project.serviceBlocks?.validator?.link || '',
      analyticsEnabled: project.serviceBlocks?.analytics?.enabled ?? true,
      analyticsTitle: project.serviceBlocks?.analytics?.title || 'DUSKPROOF Analytics Dashboard',
      analyticsDescription: project.serviceBlocks?.analytics?.description || 'See Beyond the Metrics. An advanced analytics suite delivering insights into validator performance, uptime, governance, and on-chain data availability — all in one unified view.',
      analyticsLink: project.serviceBlocks?.analytics?.link || '',
    });
    setShowForm(true);
  };

  // Функція для автоматичного підтягування всіх даних проекту (тільки точний збіг)
  const findProjectData = (projectName: string) => {
    if (!projectName.trim()) {
      console.log('🔍 Empty project name');
      return null;
    }
    
    if (!existingProjects.length) {
      console.log('🔍 No existing projects loaded yet, total projects:', existingProjects.length);
      return null;
    }
    
    console.log('🔍 Searching for project data:', projectName, 'in', existingProjects.length, 'total projects');
    
    // Фільтруємо тільки НЕ Stake проекти для автозаповнення
    const nonStakeProjects = existingProjects.filter(p => p.category !== 'Stake');
    console.log('🔍 Non-Stake projects available:', nonStakeProjects.length);
    console.log('🔍 Available projects for auto-detection:', nonStakeProjects.map(p => `"${p.title}" (${p.category})`));
    
    // Шукаємо проект ТІЛЬКИ з точною назвою (без урахування регістру) серед НЕ Stake проектів
    const matchingProject = nonStakeProjects.find(project => 
      project.title && project.title.toLowerCase() === projectName.toLowerCase()
    );
    
    if (matchingProject) {
      console.log('🎯 Exact match found for:', projectName, '→', matchingProject.title, '(category:', matchingProject.category + ')');
      return matchingProject;
    }
    
    console.log('❌ No exact match for:', projectName, '- searched in titles:', nonStakeProjects.map(p => p.title));
    return null;
  };

  // Функція для автоматичного підтягування логотипу (зворотна сумісність)
  const findProjectLogo = (projectName: string) => {
    const projectData = findProjectData(projectName);
    if (projectData && projectData.imageUrl) {
      return convertToFullUrl(projectData.imageUrl);
    }
    return '';
  };

  // Функція для отримання доступних логотипів (тільки з Services, не Stake)
  const getAvailableLogos = () => {
    return existingProjects
      .filter(project => project.imageUrl && project.category !== 'Stake')
      .map(project => ({
        title: project.title,
        imageUrl: convertToFullUrl(project.imageUrl),
        category: project.category
      }));
  };

  // Функція для конвертації локального шляху в URL через проксі
  const convertToFullUrl = (imageUrl: string) => {
    if (!imageUrl) return '';
    
    // Якщо це вже external URL (http/https), повернути як-є
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      console.log('convertToFullUrl (external):', imageUrl, '=>', imageUrl);
      return imageUrl;
    }
    
    // Якщо це вже повний backend API URL, повернути як-є
    if (imageUrl.includes('/api/images/')) {
      console.log('convertToFullUrl (api):', imageUrl, '=>', imageUrl);
      return imageUrl;
    }
    
    // Якщо це локальний шлях типу /uploads/images/projects/logos/..., конвертувати в /api/images/projects/logos/...
    if (imageUrl.includes('/uploads/images/projects/logos/')) {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const filename = imageUrl.split('/').pop() || '';
      const fullUrl = `${apiUrl}/api/images/projects/logos/${filename}`;
      console.log('convertToFullUrl (project logo):', imageUrl, '=>', fullUrl);
      return fullUrl;
    }
    
    // Якщо це локальний шлях типу /uploads/..., конвертувати в /api/images/...
    if (imageUrl.includes('/uploads/')) {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      // Витягуємо filename з шляху
      const filename = imageUrl.split('/').pop() || '';
      const fullUrl = `${apiUrl}/api/images/${filename}`;
      console.log('convertToFullUrl (local):', imageUrl, '=>', fullUrl);
      return fullUrl;
    }
    
    // Якщо це просто filename, конвертувати в /api/images/filename
    const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
    const fullUrl = `${apiUrl}/api/images/${imageUrl}`;
    console.log('convertToFullUrl (filename):', imageUrl, '=>', fullUrl);
    return fullUrl;
  };

  // Обробник зміни назви проекту з автозаповненням всіх полів
  const handleTitleChange = (newTitle: string) => {
    const projectData = findProjectData(newTitle);
    
    if (projectData && projectData.category !== 'Stake') {
      // Автозаповнення тільки з проектів НЕ Stake категорії (з Services)
      console.log('🔄 Auto-filling all fields from Services project:', projectData.title);
      console.log('🔍 Project data:', {
        projectUrl: projectData.projectUrl,
        website: projectData.links?.website,
        title: projectData.title,
        category: projectData.category
      });
      setFormData(prev => ({
        ...prev,
        title: newTitle,
        description: projectData.description || prev.description,
        imageUrl: projectData.imageUrl ? convertToFullUrl(projectData.imageUrl) : prev.imageUrl,
        // URLs з різних джерел
        projectUrl: projectData.projectUrl || projectData.links?.website || prev.projectUrl || '',
        githubUrl: projectData.links?.github || prev.githubUrl || '',
        twitterUrl: projectData.links?.twitter || prev.twitterUrl || '',
        discordUrl: projectData.links?.discord || prev.discordUrl || '',
        websiteUrl: projectData.links?.website || prev.websiteUrl || '',
        // Tags якщо є
        tags: projectData.tags || prev.tags || []
      }));
    } else {
      // Тільки змінюємо title, якщо проект не знайдено або це вже Stake проект
      setFormData(prev => ({
        ...prev,
        title: newTitle
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Валідація обов'язкових полів
    if (!formData.title.trim()) {
      alert('Please enter a project name');
      return;
    }
    
    if (!formData.description.trim()) {
      alert('Please enter a description');
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      // 🔐 SECURITY: Token is in httpOnly cookie - sent automatically
      
      const projectData = {
        title: formData.title,
        description: formData.description,
        imageUrl: formData.imageUrl,
        network: formData.network,
        status: 'active',
        isPublished: formData.isPublished,
        featured: formData.featured,
        category: 'Stake',
        projectUrl: formData.projectUrl,
        links: {
          github: formData.githubUrl,
          twitter: formData.twitterUrl,
          discord: formData.discordUrl,
          website: formData.websiteUrl,
        },
        tags: formData.tags,
        // Staking specific fields
        commission: formData.commission,
        apy: formData.apy,
        minimumStake: formData.minimumStake,
        activationPeriod: formData.activationPeriod,
        undelegatePeriod: formData.undelegatePeriod,
        stakerAddress: formData.stakerAddress,
        stakingLink: formData.stakingLink,
        ticker: formData.ticker,
        // Price API fields
        enablePriceAPI: formData.enablePriceAPI,
        coingeckoId: formData.coingeckoId,
        // Service blocks configuration
        serviceBlocks: {
          explorer: {
            enabled: formData.explorerEnabled,
            title: formData.explorerTitle,
            description: formData.explorerDescription,
            link: formData.explorerLink,
          },
          monitoring: {
            enabled: formData.monitoringEnabled,
            title: formData.monitoringTitle,
            description: formData.monitoringDescription,
            link: formData.monitoringLink,
          },
          validator: {
            enabled: formData.validatorEnabled,
            title: formData.validatorTitle,
            description: formData.validatorDescription,
            link: formData.validatorLink,
          },
          analytics: {
            enabled: formData.analyticsEnabled,
            title: formData.analyticsTitle,
            description: formData.analyticsDescription,
            link: formData.analyticsLink,
          },
        },
      };

      if (editingProject) {
        // Редагуємо існуючий проект в базі даних
        const response = await axios.put(`${apiUrl}/api/projects/${editingProject._id}`, projectData, {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });

        if (response.data.success) {
          // Логуємо повну структуру відповіді для діагностики
          console.log('Full API response:', response.data);
          
          // Оновлюємо локальний стан - обробляємо різні структури відповіді
          const updatedProject = response.data.data.project || response.data.data;
          
          if (updatedProject && updatedProject._id) {
            setStakeProjects(prev => {
              const newProjects = prev.map(project => 
                project._id === editingProject._id ? updatedProject : project
              );
              console.log('Updated projects in state:', newProjects.length);
              return newProjects;
            });
            console.log('Stake project updated in database:', updatedProject);
          } else {
            // Якщо структура незрозуміла, перезавантажуємо список
            console.warn('Unexpected response structure, reloading projects list. Response:', response.data);
            await fetchStakeProjects();
          }
          
          alert('Stake project updated successfully!');
          setEditingProject(null);
        }
      } else {
        // Створюємо новий проект в базі даних
        // 🔐 SECURITY: Use httpOnly cookies for auth
        const response = await axios.post(`${apiUrl}/api/projects`, projectData, {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true
        });

        if (response.data.success) {
          // Додаємо до локального стану
          const newProject = response.data.data.project || response.data.data;
          
          // Перевіряємо, що новий проект має необхідні поля
          if (newProject && newProject.title) {
            setStakeProjects(prev => [...prev, newProject]);
            console.log('Stake project created in database:', newProject);
            alert(`Stake project created successfully! Page available at: /stake/${newProject.projectUrl || newProject.title}`);
          } else {
            console.error('Invalid project data received:', newProject);
            alert('Project created but there was an issue with the data structure.');
          }
        }
      }
      
      setShowForm(false);
      clearForm();
    } catch (error) {
      console.error('Error saving stake project:', error);
      alert('Error saving stake project. Please try again.');
    }
  };

  const handleDelete = async (projectId: string) => {
    if (window.confirm('Are you sure you want to delete this stake project?')) {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        
        const response = await axios.delete(`${apiUrl}/api/projects/${projectId}`, {
          withCredentials: true
        });

        if (response.data.success) {
          // Видаляємо з локального стану та перезавантажуємо список для синхронізації
          setStakeProjects(prev => prev.filter(project => project._id !== projectId));
          await fetchStakeProjects(); // Додаткова синхронізація
          alert('Stake project deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting stake project:', error);
        alert('Error deleting project. Please try again.');
      }
    }
  };

  // Фільтрація проектів за пошуковим запитом
  const filteredProjects = stakeProjects.filter(project =>
    (project.title && project.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );



  return (
    <div className="text-gray-100 font-sans h-full" style={{backgroundColor: '#0a0908', padding: '2rem'}}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-dusk-amber to-dusk-copper bg-clip-text text-transparent mb-2">Stake Management</h1>
            <p className="text-gray-400">Manage blockchain infrastructure services and guides</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50 focus:border-dusk-copper transition-all"
              />
              <svg 
                className="absolute left-3 top-3.5 h-5 w-5 text-[var(--dusk-faint)]" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            {/* Update Stake button - show only when editing */}
            {editingProject && showForm && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(e);
                }}
                className="px-6 py-3 bg-gradient-to-r from-dusk-copper-deep to-dusk-copper hover:from-dusk-copper hover:to-dusk-amber text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/30 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Update Stake
              </button>
            )}
            
            <button
              onClick={() => {
                if (!showForm) {
                  // Якщо відкриваємо форму для нового проекту, очищуємо її
                  setEditingProject(null);
                  clearForm();
                }
                toggleForm();
              }}
              className="px-6 py-3 bg-gradient-to-r from-dusk-copper to-dusk-amber hover:from-dusk-copper-deep hover:to-dusk-copper text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/40"
            >
              {showForm ? (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Cancel
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Stake
                </>
              )}
            </button>
          </div>
        </div>

        {/* Add Stake Form */}
        {showForm && (
          <div className="p-8 mb-8 rounded-2xl border border-[var(--dusk-line)] bg-[rgba(243,235,226,0.04)] backdrop-blur-sm">
            <h2 className="text-xl font-bold text-white mb-6">
              {editingProject ? 'Edit Stake Project' : 'Add New Stake Project'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Project Name</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                    placeholder="e.g., Starknet (logo will auto-detect)"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {projectsLoading ? (
                      "� Loading existing projects..."
                    ) : existingProjects.length > 0 ? (
                      `💡 Auto-detection available (${existingProjects.length} projects loaded)`
                    ) : (
                      "💡 No existing projects found for auto-detection"
                    )}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                  placeholder="Enter project description"
                />
              </div>

              {/* Project URLs Section */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Project URL</label>
                    <input
                      type="text"
                      value={formData.projectUrl}
                      onChange={(e) => setFormData({ ...formData, projectUrl: e.target.value })}
                      className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                      placeholder="starknet"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">GitHub URL</label>
                    <input
                      type="url"
                      value={formData.githubUrl}
                      onChange={(e) => setFormData({ ...formData, githubUrl: e.target.value })}
                      className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                      placeholder="https://github.com/keep-starknet-strange/awesome-starknet"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Twitter URL</label>
                    <input
                      type="url"
                      value={formData.twitterUrl}
                      onChange={(e) => setFormData({ ...formData, twitterUrl: e.target.value })}
                      className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                      placeholder="https://twitter.com/Starknet"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Discord URL</label>
                    <input
                      type="url"
                      value={formData.discordUrl}
                      onChange={(e) => setFormData({ ...formData, discordUrl: e.target.value })}
                      className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                      placeholder="https://discord.gg/starknet-community"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Website URL</label>
                  <input
                    type="url"
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                    className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                    placeholder="https://www.starknet.io"
                  />
                </div>
              </div>

              {/* Tags Section */}
              <div>
                <TagInput
                  value={formData.tags}
                  onChange={(newTags) => setFormData({ ...formData, tags: newTags })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Logo
                  {formData.imageUrl && findProjectLogo(formData.title) === formData.imageUrl && (
                    <span className="ml-2 text-xs text-green-400">✓ Auto-detected</span>
                  )}
                </label>
                
                <div className="space-y-3">
                  {/* Logo Preview */}
                  {formData.imageUrl && (
                    <div className="flex items-center space-x-3 p-3 bg-[rgba(243,235,226,0.06)]/50 rounded-lg border border-[var(--dusk-line-strong)]">
                      <img
                        src={convertToFullUrl(formData.imageUrl)}
                        alt="Logo preview"
                        className="w-12 h-12 rounded-lg object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <div className="flex-1">
                        <div className="text-sm text-gray-300">Current logo</div>
                        <div className="text-xs text-gray-400 break-all">{formData.imageUrl}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, imageUrl: '' })}
                        className="text-red-400 hover:text-red-300 p-1"
                        title="Remove logo"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowLogoSelector(true)}
                      className="flex-1 px-4 py-2 bg-dusk-copper hover:bg-dusk-copper-deep text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Choose from Existing
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const url = prompt('Enter logo URL:');
                        if (url) setFormData({ ...formData, imageUrl: url });
                      }}
                      className="flex-1 px-4 py-2 bg-gray-600 hover:bg-[rgba(243,235,226,0.06)] text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Custom URL
                    </button>
                  </div>
                  
                  {/* Hidden field for form validation */}
                  <input 
                    type="hidden" 
                    name="imageUrl" 
                    value={formData.imageUrl} 
                  />
                </div>
              </div>



              {/* Staking Parameters */}
              <div className="border-t border-[var(--dusk-line-strong)] pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Staking Parameters</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Commission</label>
                    <input
                      type="text"
                      value={formData.commission}
                      onChange={(e) => setFormData({ ...formData, commission: e.target.value })}
                      className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                      placeholder="e.g., 5%"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">APY</label>
                    <input
                      type="text"
                      value={formData.apy}
                      onChange={(e) => setFormData({ ...formData, apy: e.target.value })}
                      className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                      placeholder="e.g., 12.5%"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Minimum Stake</label>
                    <input
                      type="text"
                      value={formData.minimumStake}
                      onChange={(e) => setFormData({ ...formData, minimumStake: e.target.value })}
                      className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                      placeholder="e.g., 100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Activation Period</label>
                    <input
                      type="text"
                      value={formData.activationPeriod}
                      onChange={(e) => setFormData({ ...formData, activationPeriod: e.target.value })}
                      className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                      placeholder="e.g., 7 days"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Undelegate Period</label>
                    <input
                      type="text"
                      value={formData.undelegatePeriod}
                      onChange={(e) => setFormData({ ...formData, undelegatePeriod: e.target.value })}
                      className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                      placeholder="e.g., 14 days"
                    />
                  </div>
                </div>
              </div>

              {/* Delegation Information - Separate Block */}
              <div className="border-t border-[var(--dusk-line-strong)] pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Delegation Information</h3>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Staker Address
                    </label>
                    <input
                      type="text"
                      value={formData.stakerAddress}
                      onChange={(e) => setFormData({ ...formData, stakerAddress: e.target.value })}
                      className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                      placeholder="cosmosvaloper1abc..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Staking Link
                    </label>
                    <input
                      type="url"
                      value={formData.stakingLink}
                      onChange={(e) => setFormData({ ...formData, stakingLink: e.target.value })}
                      className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                      placeholder="https://wallet.keplr.app/chains/cosmos-hub/stake..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Ticker
                    </label>
                    <input
                      type="text"
                      value={formData.ticker}
                      onChange={(e) => setFormData({ ...formData, ticker: e.target.value })}
                      className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                      placeholder="STRK"
                    />
                  </div>
                </div>
              </div>

              {/* Price API Configuration - New Block */}
              <div className="border-t border-[var(--dusk-line-strong)] pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Price API Configuration</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="enablePriceAPI"
                      checked={formData.enablePriceAPI}
                      onChange={(e) => setFormData({ ...formData, enablePriceAPI: e.target.checked })}
                      className="w-4 h-4 text-dusk-copper bg-[rgba(243,235,226,0.06)] border-[var(--dusk-line-strong)] rounded focus:ring-dusk-copper focus:ring-2"
                    />
                    <label htmlFor="enablePriceAPI" className="text-sm font-medium text-gray-300">
                      Enable automatic price fetching from CoinGecko API
                    </label>
                  </div>
                  
                  {formData.enablePriceAPI && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        CoinGecko ID
                        {!editingProject && (
                          <span className="text-xs text-dusk-amber ml-2">(auto-generated)</span>
                        )}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.coingeckoId}
                          onChange={(e) => setFormData({ ...formData, coingeckoId: e.target.value })}
                          className="flex-1 px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                          placeholder="starknet"
                        />
                        {!editingProject && formData.title.trim() && (
                          <button
                            type="button"
                            onClick={() => {
                              const generateCoinGeckoId = (title: string) => {
                                return title
                                  .toLowerCase()
                                  .replace(/[^a-z0-9\s-]/g, '')
                                  .replace(/\s+/g, '-')
                                  .replace(/-+/g, '-')
                                  .replace(/^-+|-+$/g, '');
                              };
                              const newId = generateCoinGeckoId(formData.title);
                              setFormData({ ...formData, coingeckoId: newId });
                            }}
                            className="px-3 py-2 bg-dusk-copper hover:bg-dusk-copper-deep text-white rounded-lg text-sm transition-colors"
                            title="Regenerate from project name"
                          >
                            ↻
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {!editingProject 
                          ? "Auto-generated from project name. Edit if needed for correct CoinGecko ID."
                          : "ID from CoinGecko URL (e.g., \"starknet\" for StarkNet)"
                        }
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Service Blocks Configuration */}
              <div className="border-t border-[var(--dusk-line-strong)] pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Service Blocks Configuration</h3>
                <div className="space-y-6">
                  {/* Explorer Block */}
                  <div className="bg-[rgba(243,235,226,0.06)] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-white">DUSKPROOF Blockchain Explorer</h4>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.explorerEnabled}
                          onChange={(e) => setFormData({ ...formData, explorerEnabled: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-300">Enabled</span>
                      </label>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                        <input
                          type="text"
                          value={formData.explorerTitle}
                          onChange={(e) => setFormData({ ...formData, explorerTitle: e.target.value })}
                          className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                          placeholder="DUSKPROOF Blockchain Explorer"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Link</label>
                        <input
                          type="url"
                          value={formData.explorerLink}
                          onChange={(e) => setFormData({ ...formData, explorerLink: e.target.value })}
                          className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                          placeholder="https://explorer.example.com"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                      <textarea
                        value={formData.explorerDescription}
                        onChange={(e) => setFormData({ ...formData, explorerDescription: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:outline-none focus:ring-2 focus:ring-dusk-copper/50"
                        placeholder="Explore with Precision. A next-gen explorer built for seamless navigation across networks — track blocks, validators, and delegations with clarity and speed."
                      />
                    </div>
                  </div>

                  {/* Monitoring Block */}
                  <div className="rounded-2xl border border-[var(--dusk-line)] bg-gradient-to-br from-[rgba(243,235,226,0.04)] to-[rgba(243,235,226,0.02)] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-white">DUSKPROOF Alert System</h4>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.monitoringEnabled}
                          onChange={(e) => setFormData({ ...formData, monitoringEnabled: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-300">Enabled</span>
                      </label>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                        <input
                          type="text"
                          value={formData.monitoringTitle}
                          onChange={(e) => setFormData({ ...formData, monitoringTitle: e.target.value })}
                          className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:ring-2 focus:ring-dusk-copper/50"
                          placeholder="DUSKPROOF Alert System"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Link</label>
                        <input
                          type="url"
                          value={formData.monitoringLink}
                          onChange={(e) => setFormData({ ...formData, monitoringLink: e.target.value })}
                          className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:ring-2 focus:ring-dusk-copper/50"
                          placeholder="https://monitoring.example.com"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                      <textarea
                        value={formData.monitoringDescription}
                        onChange={(e) => setFormData({ ...formData, monitoringDescription: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:ring-2 focus:ring-dusk-copper/50 resize-none"
                        placeholder="Stay Informed, Instantly. AI-enhanced Telegram bot that monitors your validator, alerts you about missed blocks, version updates, and governance proposals — fully customizable for your setup."
                      />
                    </div>
                  </div>

                  {/* Validator Block */}
                  <div className="rounded-2xl border border-[var(--dusk-line)] bg-gradient-to-br from-[rgba(243,235,226,0.04)] to-[rgba(243,235,226,0.02)] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-white">DUSKPROOF Blockchain Services</h4>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.validatorEnabled}
                          onChange={(e) => setFormData({ ...formData, validatorEnabled: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-300">Enabled</span>
                      </label>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                        <input
                          type="text"
                          value={formData.validatorTitle}
                          onChange={(e) => setFormData({ ...formData, validatorTitle: e.target.value })}
                          className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:ring-2 focus:ring-dusk-copper/50"
                          placeholder="DUSKPROOF Blockchain Services"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Link</label>
                        <input
                          type="url"
                          value={formData.validatorLink}
                          onChange={(e) => setFormData({ ...formData, validatorLink: e.target.value })}
                          className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:ring-2 focus:ring-dusk-copper/50"
                          placeholder="https://validator.example.com"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                      <textarea
                        value={formData.validatorDescription}
                        onChange={(e) => setFormData({ ...formData, validatorDescription: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:ring-2 focus:ring-dusk-copper/50 resize-none"
                        placeholder="We maintain a full suite of infrastructure for mainnets and testnets — including RPC, API, gRPC, snapshots, and state sync. Secure, stable, and constantly updated."
                      />
                    </div>
                  </div>

                  {/* Analytics Block */}
                  <div className="rounded-2xl border border-[var(--dusk-line)] bg-gradient-to-br from-[rgba(243,235,226,0.04)] to-[rgba(243,235,226,0.02)] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-white">DUSKPROOF Analytics Dashboard</h4>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.analyticsEnabled}
                          onChange={(e) => setFormData({ ...formData, analyticsEnabled: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-300">Enabled</span>
                      </label>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                        <input
                          type="text"
                          value={formData.analyticsTitle}
                          onChange={(e) => setFormData({ ...formData, analyticsTitle: e.target.value })}
                          className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:ring-2 focus:ring-dusk-copper/50"
                          placeholder="DUSKPROOF Analytics Dashboard"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Link</label>
                        <input
                          type="url"
                          value={formData.analyticsLink}
                          onChange={(e) => setFormData({ ...formData, analyticsLink: e.target.value })}
                          className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:ring-2 focus:ring-dusk-copper/50"
                          placeholder="https://analytics.example.com"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                      <textarea
                        value={formData.analyticsDescription}
                        onChange={(e) => setFormData({ ...formData, analyticsDescription: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-3 bg-[rgba(243,235,226,0.04)] border border-[var(--dusk-line)] rounded-xl text-white placeholder-[var(--dusk-faint)] focus:ring-2 focus:ring-dusk-copper/50 resize-none"
                        placeholder="See Beyond the Metrics. An advanced analytics suite delivering insights into validator performance, uptime, governance, and on-chain data availability — all in one unified view."
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Visibility */}
              <div className="border-t border-[var(--dusk-line-strong)] pt-6">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-gray-300">Visibility:</span>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="adminOnly"
                        name="visibility"
                        checked={!formData.isPublished}
                        onChange={() => setFormData({ ...formData, isPublished: false })}
                        className="mr-2"
                      />
                      <label htmlFor="adminOnly" className="text-sm font-medium text-gray-300 mr-4">
                        Admin Only
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="radio"
                        id="public"
                        name="visibility"
                        checked={formData.isPublished}
                        onChange={() => setFormData({ ...formData, isPublished: true })}
                        className="mr-2"
                      />
                      <label htmlFor="public" className="text-sm font-medium text-gray-300">
                        Public
                      </label>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="featured"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="mr-2"
                    />
                    <label htmlFor="featured" className="text-sm font-medium text-gray-300">
                      Featured Project
                    </label>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex space-x-4 pt-6 border-t border-[var(--dusk-line-strong)]">
                <button
                  type="submit"
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors"
                >
                  {editingProject ? 'Update Stake Project' : 'Add Stake Project'}
                </button>
                <button
                  type="button"
                  onClick={toggleForm}
                  className="px-6 py-2 bg-[rgba(243,235,226,0.06)] hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Services Table */}
        <div className="rounded-2xl border border-[var(--dusk-line)] overflow-hidden bg-[rgba(243,235,226,0.03)] backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[rgba(243,235,226,0.05)] border-b border-[var(--dusk-line)]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Visibility
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {filteredProjects.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center">
                      <div className="text-gray-400">
                        <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2M4 13h2m0 0V9a2 2 0 012-2h2m0 0V6a2 2 0 012-2h2" />
                        </svg>
                        <p className="text-lg font-medium">
                          {stakeProjects.length === 0 ? 'No stake projects yet' : 'No projects found'}
                        </p>
                        <p className="text-sm">
                          {stakeProjects.length === 0 ? 'Click "Add Stake" to create your first stake project' : 'Try adjusting your search terms'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredProjects.map((project) => (
                  <tr key={project._id} className="hover:bg-[rgba(243,235,226,0.05)] transition-all duration-300 border-[var(--dusk-line)]">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                        {project.imageUrl ? (
                          <img
                            className="h-10 w-10 rounded-lg object-cover"
                            src={convertToFullUrl(project.imageUrl)}
                            alt={project.title}
                            onError={(e) => {
                              console.log('Image load error:', project.imageUrl, 'Full URL:', convertToFullUrl(project.imageUrl || ''));
                              e.currentTarget.style.display = 'none';
                              // Показуємо fallback
                              e.currentTarget.parentElement!.innerHTML = `
                                <div class="h-10 w-10 rounded-lg bg-gray-600 flex items-center justify-center">
                                  <svg class="h-6 w-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                                  </svg>
                                </div>
                              `;
                            }}
                            onLoad={() => {
                              console.log('Image loaded successfully:', convertToFullUrl(project.imageUrl || ''));
                            }}
                          />
                        ) : (
                            <div className="h-10 w-10 rounded-lg bg-gray-600 flex items-center justify-center">
                              <svg className="h-6 w-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">
                            {project.title}
                          </div>
                          <div className="text-sm text-gray-400 max-w-xs truncate">
                            {project.description}
                          </div>
                          <div className="flex gap-2 mt-1">
                            <span className="text-xs text-dusk-amber">APY: {project.apy}</span>
                            <span className="text-xs text-green-400">Min: {project.minimumStake}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`admin-badge ${
                          project.status === 'inactive'
                            ? 'admin-badge-status-inactive'
                            : project.status === 'maintenance'
                              ? 'admin-badge-status-maintenance'
                              : 'admin-badge-status-active'
                        }`}
                      >
                        {project.status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col items-start gap-1.5">
                        <span
                          className={`admin-badge ${
                            project.isPublished
                              ? 'admin-badge-visibility-public'
                              : 'admin-badge-visibility-private'
                          }`}
                        >
                          {project.isPublished ? 'Public' : 'Admin Only'}
                        </span>
                        {project.featured && (
                          <span className="admin-badge admin-badge-featured">Featured</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            const projectUrlPart = project.projectUrl?.split('/').pop() || project.projectUrl || (project.title ? project.title.toLowerCase().replace(/[^a-z0-9 -]/g, '').replace(/\s+/g, '-') : 'untitled');
                            const stakeUrl = `/stake/${projectUrlPart}`;
                            window.open(stakeUrl, '_blank');
                          }}
                          className="p-2 text-dusk-amber hover:text-dusk-amber hover:bg-[rgba(208,0,104,0.15)] rounded-lg transition-all"
                          title="View Page"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleEdit(project)}
                          className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all"
                          title="Edit"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(project._id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all"
                          title="Delete"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Logo Selector Modal */}
      {showLogoSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[rgba(243,235,226,0.04)] rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">Choose Logo</h3>
              <button
                onClick={() => setShowLogoSelector(false)}
                className="text-gray-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {getAvailableLogos().map((logo, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setFormData({ ...formData, imageUrl: logo.imageUrl });
                    setShowLogoSelector(false);
                  }}
                  className="group p-3 bg-[rgba(243,235,226,0.06)] hover:bg-gray-600 rounded-lg transition-colors"
                >
                  <img
                    src={logo.imageUrl}
                    alt={logo.title}
                    className="w-full h-16 object-contain rounded mb-2"
                    onError={(e) => {
                      e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIGZpbGw9IiM2MzYzNzAiIHZpZXdCb3g9IjAgMCAyMCAyMCI+PHBhdGggZD0iTTQgM2ExIDEgMCAwMC0xIDF2MTJhMSAxIDAgMDAxIDFoMTJhMSAxIDAgMDAxLTFWNGExIDEgMCAwMC0xLTFINHptOSA4YTEgMSAwIDAxLTEgMWgtNGExIDEgMCAwMS0xLTFWOGExIDEgMCAwMTEtMWg0YTEgMSAwIDAxMSAxdjN6Ii8+PC9zdmc+';
                    }}
                  />
                  <div className="text-xs text-gray-300 group-hover:text-white text-center truncate">
                    {logo.title}
                    {logo.category && <div className="text-xs text-[var(--dusk-faint)]">({logo.category})</div>}
                  </div>
                </button>
              ))}
            </div>
            
            {getAvailableLogos().length === 0 && (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">No logos available from Services</div>
                <div className="text-sm text-[var(--dusk-faint)]">
                  Add projects with logos to your Services section first
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StakeSection;
